import { USER_ROLES } from "@/app/app.constants";
import MODULES from "@/config/modules.config";
import {
  AnalyticsUpIcon,
  AssignmentsIcon,
  CommandIcon,
  Configuration01Icon,
  DashboardCircleEditIcon,
  DashboardSpeed01Icon,
  DepartementIcon,
  DocumentValidationIcon,
  DollarSignIcon,
  FileBarChartIcon,
  FileTextIcon,
  FingerAccessIcon,
  GitBranchIcon,
  HandIcon,
  JobSearchIcon,
  LayoutDashboardIcon,
  MentoringIcon,
  Parabola03Icon,
  TaskDaily01Icon,
  UserQuestion02Icon,
  UsersIcon,
  VideoCameraAiIcon,
} from "@/icons";
import ControlPanelDashboard from "@/pages/Admin/ControlPanel/Dashboard/ControlPanelDashboard";
import Organization from "@/pages/Admin/ControlPanel/Organization/Organization";
import Policies from "@/pages/Admin/ControlPanel/Policies/Policies";
import RolePermission from "@/pages/Admin/ControlPanel/RolePermission/RolePermission";
import EmpAnalytics from "@/pages/Admin/Employee/Analytics/EmpAnalytics";
import EmpAttendance from "@/pages/Admin/Employee/Attendance/EmpAttendance";
import EmpDashboard from "@/pages/Admin/Employee/Dashboard/EmpDashboard";
import EmployeeDetails from "@/pages/Admin/Employee/Dashboard/EmployeeDetails";
import UpdateEmployee from "@/pages/Admin/Employee/Dashboard/UpdateEmployee";
import EmpLeave from "@/pages/Admin/Employee/Leave/EmpLeave";
import Report from "@/pages/Admin/Employee/More/Report/Report";
import EmpOnboard from "@/pages/Admin/Employee/Onboard/EmpOnboard";
import BatchDisbursedRecords from "@/pages/Admin/Employee/Payroll/_components/LeaveEncashment/BatchDisbursedRecords";
import BatchEncashments from "@/pages/Admin/Employee/Payroll/_components/LeaveEncashment/BatchEncashments";
import BatchPayrolls from "@/pages/Admin/Employee/Payroll/_components/PayrollGenerate/BatchPayrolls";
import BatchSalarySheet from "@/pages/Admin/Employee/Payroll/_components/PayrollGenerate/BatchSalarySheet";
import EmpPayroll from "@/pages/Admin/Employee/Payroll/EmpPayroll";
import EmpPerformance from "@/pages/Admin/Employee/Performance/EmpPerformance";
import Assessments from "@/pages/Admin/Recruitment/Assessments/Assessments";
import RecruitmentDashboard from "@/pages/Admin/Recruitment/Dashboard/RecruitmentDashboard";
import Evaluation from "@/pages/Admin/Recruitment/Evaluation/Evaluation";
import HiringPipeline from "@/pages/Admin/Recruitment/Hiring/HiringPipeline";
import Interviews from "@/pages/Admin/Recruitment/Interviews/Interviews";
import TalentHuntDetails from "@/pages/Admin/Recruitment/JobsApplicants/_components/TalentHunt/TalentHuntDetails";
import JobApplicants from "@/pages/Admin/Recruitment/JobsApplicants/JobApplicants";
import Login from "@/pages/Auth/Login";
import PaymentFailed from "@/pages/Auth/PaymentFailed";
import PaymentSuccess from "@/pages/Auth/PaymentSuccess";
import SelfOnboard2 from "@/pages/Auth/SelfOnboard/SelfOnboard";
import VerifyOTP from "@/pages/Auth/VerifyOTP";
import SelectOrganization from "@/pages/Auth/SelectOrganization";
import NotFound from "@/pages/NotFound";
import EmployeeAppraisal from "@/pages/Performance/EmployeeAppraisal";
import EmployeeKPI from "@/pages/Performance/EmployeeKPI";
import LineManagerAppraisal from "@/pages/Performance/LineManagerAppraisal";
import LineManagerKPI from "@/pages/Performance/LineManagerKPI";
import type { AppRoute } from "@/types/global.types";
import { type ElementType } from "react";

// App routes
export const APP_ROUTES: AppRoute[] = [
  // ------ Routes for Resident & Staff (ADMIN) ------
  {
    label: "Employee",
    icon: UsersIcon,
    path: "/admin/employee",
    roles: [USER_ROLES.ADMIN],
    moduleId: MODULES.EMPLOYEE,
    children: [
      {
        label: "Dashboard",
        element: EmpDashboard,
        icon: LayoutDashboardIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/dashboard",
        moduleId: MODULES.EMPLOYEE_DASHBOARD,
      },
      {
        element: EmployeeDetails,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/dashboard/employee-details/:id",
        moduleId: MODULES.EMPLOYEE_DASHBOARD,
      },
      {
        element: UpdateEmployee,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/dashboard/employee-details/:id/manage",
        moduleId: MODULES.EMPLOYEE_DASHBOARD,
      },
      {
        label: "Attendance",
        element: EmpAttendance,
        icon: HandIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/attendance",
        moduleId: MODULES.EMPLOYEE_ATTENDANCE,
      },
      {
        label: "Leave",
        element: EmpLeave,
        icon: UserQuestion02Icon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/leave",
        moduleId: MODULES.EMPLOYEE_LEAVE,
      },
      {
        label: "Payroll",
        element: EmpPayroll,
        icon: DollarSignIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/payroll",
        moduleId: MODULES.EMPLOYEE_PAYROLL,
      },
      // Salary Sheet Routes
      {
        element: BatchPayrolls,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/payroll/batch/:id",
        moduleId: MODULES.EMPLOYEE_PAYROLL,
      },
      {
        element: BatchSalarySheet,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/payroll/batch/:id/salary-sheet",
        moduleId: MODULES.EMPLOYEE_PAYROLL,
      },

      // Leave Encashment Routes
      {
        element: BatchEncashments,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/payroll/encashment-batch/:id",
        moduleId: MODULES.EMPLOYEE_PAYROLL,
      },
      {
        element: BatchDisbursedRecords,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/payroll/encashment-batch/:id/disburse-records",
        moduleId: MODULES.EMPLOYEE_PAYROLL,
      },

      {
        label: "System Analytics",
        element: EmpAnalytics,
        icon: CommandIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/system-analytics",
        moduleId: MODULES.EMPLOYEE_SYSTEM_ANALYTICS,
      },
      {
        label: "Onboard Management",
        element: EmpOnboard,
        icon: FileTextIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/onboard-management",
        moduleId: MODULES.EMPLOYEE_ONBOARD_MANAGEMENT,
      },
      {
        element: EmpPerformance,
        roles: [USER_ROLES.ADMIN],
        icon: AnalyticsUpIcon,
        label: "Performance Management",
        path: "/admin/employee/performance-management",
        moduleId: MODULES.EMPLOYEE_PERFORMANCE,
        showOnMore: true,
      },
      {
        element: Report,
        icon: FileBarChartIcon,
        label: "Reports",
        roles: [USER_ROLES.ADMIN],
        path: "/admin/employee/report",
        moduleId: MODULES.EMPLOYEE_REPORTS,
        showOnMore: true,
      },
    ],
  },
  // ------ Routes for Control Panel ------
  {
    label: "Recruitment",
    icon: TaskDaily01Icon,
    path: "/admin/recruitment",
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.EVALUATOR,
      USER_ROLES.LINE_MANAGER,
      USER_ROLES.EVALUATOR_MANAGER,
      USER_ROLES.EVALUATOR_USER,
    ],
    moduleId: MODULES.RECRUITMENT,
    children: [
      {
        label: "Dashboard",
        element: RecruitmentDashboard,
        icon: LayoutDashboardIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/dashboard",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        label: "Job & Applicants",
        element: JobApplicants,
        icon: JobSearchIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/job-applicants",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        element: TalentHuntDetails,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/job-applicants/talent-hunt/:id",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        label: "Assessments",
        element: Assessments,
        icon: DocumentValidationIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/assessments",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        label: "Interviews",
        element: Interviews,
        icon: VideoCameraAiIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/interviews",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        label: "Hiring Pipeline",
        element: HiringPipeline,
        icon: GitBranchIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/recruitment/hiring-pipeline",
        moduleId: MODULES.RECRUITMENT,
      },
      {
        // showOnMore:true,
        label: "Evaluation",
        element: Evaluation,
        icon: AssignmentsIcon,
        roles: [
          USER_ROLES.ADMIN,
          USER_ROLES.EVALUATOR,
          USER_ROLES.LINE_MANAGER,
          USER_ROLES.EVALUATOR_MANAGER,
          USER_ROLES.EVALUATOR_USER,
        ],
        path: "/admin/recruitment/evaluation",
        moduleId: MODULES.RECRUITMENT,
      },
    ],
  },

  // ------ Routes for Control Panel ------
  {
    label: "Control Panel",
    icon: Configuration01Icon,
    path: "/admin/control-panel",
    roles: [USER_ROLES.ADMIN],
    moduleId: MODULES.CONTROL_PANEL,
    children: [
      {
        label: "Dashboard",
        element: ControlPanelDashboard,
        icon: LayoutDashboardIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/control-panel/dashboard",
        moduleId: MODULES.CONTROL_PANEL_DASHBOARD,
      },
      {
        label: "Roles & Permissions",
        element: RolePermission,
        icon: MentoringIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/control-panel/role-permission",
        moduleId: MODULES.CONTROL_PANEL_ROLE_PERMISSION,
      },
      {
        label: "Organization",
        element: Organization,
        icon: DepartementIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/control-panel/organization",
        moduleId: MODULES.CONTROL_PANEL_ORGANIZATION,
      },
      {
        label: "Policies",
        element: Policies,
        icon: FingerAccessIcon,
        roles: [USER_ROLES.ADMIN],
        path: "/admin/control-panel/policies",
        moduleId: MODULES.CONTROL_PANEL_POLICIES,
      },
    ],
  },

  {
    label: "Performance",
    icon: DashboardSpeed01Icon,
    path: "/admin/performance",
    roles: [
      USER_ROLES.ADMIN,
      USER_ROLES.LINE_MANAGER,
      USER_ROLES.EVALUATOR_MANAGER,
    ],
    moduleId: MODULES.PERFORMANCE,
    children: [
      {
        label: "KPI",
        icon: Parabola03Icon,
        element: LineManagerKPI,
        roles: [
          USER_ROLES.ADMIN,
          USER_ROLES.LINE_MANAGER,
          USER_ROLES.EVALUATOR_MANAGER,
        ],
        path: "/admin/performance/kpi",
        moduleId: MODULES.PERFORMANCE_KPI,
      },
      {
        label: "Appraisal",
        icon: DashboardCircleEditIcon,
        element: LineManagerAppraisal,
        roles: [
          USER_ROLES.ADMIN,
          USER_ROLES.LINE_MANAGER,
          USER_ROLES.EVALUATOR_MANAGER,
        ],
        path: "/admin/performance/appraisal",
        moduleId: MODULES.PERFORMANCE_APPRAISAL,
      },
    ],
  },
  {
    label: "Performance",
    icon: DashboardSpeed01Icon,
    path: "/admin/performance",
    roles: [USER_ROLES.USER, USER_ROLES.EVALUATOR_USER],
    moduleId: MODULES.PERFORMANCE,
    children: [
      {
        label: "KPI",
        icon: Parabola03Icon,
        element: EmployeeKPI,
        roles: [USER_ROLES.USER, USER_ROLES.EVALUATOR_USER],
        path: "/admin/performance/kpi",
        moduleId: MODULES.PERFORMANCE_KPI,
      },
      {
        label: "Appraisal",
        icon: DashboardCircleEditIcon,
        element: EmployeeAppraisal,
        roles: [USER_ROLES.USER, USER_ROLES.EVALUATOR_USER],
        path: "/admin/performance/appraisal",
        moduleId: MODULES.PERFORMANCE_APPRAISAL,
      },
    ],
  },
];

// Public routers
export const PUBLIC_ROUTES: {
  path: string;
  element: ElementType;
  name?: string;
}[] = [
  {
    path: "/onboard",
    element: SelfOnboard2,
  },
  {
    path: "/login",
    element: Login,
  },
  {
    path: "/otp",
    element: VerifyOTP,
  },
  {
    path: "/select-organization",
    element: SelectOrganization,
  },
  {
    path: "/payment-success",
    element: PaymentSuccess,
  },
  {
    path: "/payment-failed",
    element: PaymentFailed,
  },
  {
    path: "*",
    element: NotFound,
  },
];
